import type { ReactNode } from "react";

import { cn } from "../../libs/cn";
import { Icon } from "./icons";

// Каркас экрана: шапка сверху, содержимое посередине, главное действие прибито к низу — туда, где
// достаёт большой палец, и на одном и том же месте во всех состояниях смены.
// tint — цвет состояния из 1С: экран «занят» заливается им целиком, чтобы статус читался издалека.
export function Screen({
  children,
  error,
  header,
  footer,
  tint,
}: {
  children: ReactNode;
  error?: string | null;
  header?: ReactNode;
  footer?: ReactNode;
  tint?: string;
}) {
  return (
    <div
      // Высота — ровно окно, а не «не меньше окна»: кнопки всегда внизу, а то, что не поместилось,
      // прокручивается внутри main. Раньше футер был sticky и на коротком экране с крупным системным
      // шрифтом (масштаб экрана + шрифт 130 %) ложился поверх диска и подсказки.
      className={cn("flex h-dvh w-full", tint && "text-white")}
      // Сначала сплошной цвет: если WebView не знает color-mix, градиент отбросится, а заливка останется.
      style={
        tint
          ? {
              backgroundColor: tint,
              backgroundImage: `linear-gradient(170deg, ${tint} 0%, color-mix(in srgb, ${tint} 62%, black) 85%)`,
            }
          : undefined
      }
    >
      <div className="mx-auto flex min-h-0 w-full max-w-md flex-col pt-[env(safe-area-inset-top)]">
        {header && (
          <header className="flex min-h-[min(3.5rem,8dvh)] shrink-0 items-center justify-between gap-3 px-4 pt-2">{header}</header>
        )}
        {error && (
          <p
            role="alert"
            className="mx-4 mt-2 rounded-xl bg-[var(--color-danger-soft)] px-4 py-3 text-sm text-[var(--color-danger)]"
          >
            {error}
          </p>
        )}
        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pt-2 pb-4">{children}</main>
        {footer && (
          <footer
            className={cn(
              "flex shrink-0 flex-col gap-2 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]",
              !tint && "bg-[var(--color-page)]/95 backdrop-blur",
              // На залитом цветом экране — лёгкое затемнение снизу: если контент всё же дотянется до
              // кнопок (совсем короткий экран), они останутся читаемыми.
              tint && "bg-linear-to-t from-black/20 to-black/0",
            )}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export function Centered({ children }: { children: ReactNode }) {
  // min-h-fit: когда контент выше main и тот прокручивается, блок не сжимается и не прячет верх диска.
  return (
    <div className="flex min-h-fit flex-1 flex-col items-center justify-center gap-3 py-[min(1.5rem,3dvh)]">{children}</div>
  );
}

export const TONES = {
  muted: "bg-black/5 text-[var(--color-muted)]",
  accent: "bg-[var(--color-accent-soft)] text-[var(--color-accent)]",
  gold: "bg-[var(--color-gold-soft)] text-[var(--color-gold)]",
  danger: "bg-[var(--color-danger-soft)] text-[var(--color-danger)]",
  // На залитом цветом экране.
  inverse: "bg-white/15 text-white",
};
export type Tone = keyof typeof TONES;

export function StatusPill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={cn("inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold", TONES[tone])}>
      <span className={cn("size-2 rounded-full bg-current", tone !== "muted" && "animate-pulse")} />
      {children}
    </span>
  );
}

export function IconBadge({ tone, name, size = "md" }: { tone: Tone; name: string | null; size?: "md" | "lg" }) {
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center",
        size === "lg" ? "size-16 rounded-full" : "size-11 rounded-xl",
        TONES[tone],
      )}
    >
      <Icon name={name} className={size === "lg" ? "size-8" : "size-6"} />
    </span>
  );
}

export function BackButton({ onClick, children = "Назад" }: { onClick: () => void; children?: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="-ml-2 inline-flex min-h-11 items-center gap-1 rounded-xl px-2 text-sm font-semibold text-[var(--color-muted)] active:bg-black/5"
    >
      <Icon name="back" className="size-5" />
      {children}
    </button>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h3 className="px-1 pt-4 pb-2 text-xs font-bold tracking-wider text-[var(--color-muted)] uppercase">{children}</h3>
  );
}

// Нижняя шторка поверх экрана: подтверждение действий, которые нельзя отменить одним тапом.
export function Sheet({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-10 flex items-end justify-center bg-black/40" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        className="flex w-full max-w-md flex-col gap-3 rounded-t-3xl bg-[var(--color-surface)] px-5 pt-6 pb-[max(1.25rem,env(safe-area-inset-bottom))]"
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
